import { useValue } from "@legendapp/state/react";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";

import { blocks$ } from "~/stores/blocks/blocks.crud";
import { boards$ } from "~/stores/boards";

/** Reactively returns the ids of every block whose `datasetId` matches. */
export const useBlocksUsingDataset = (datasetId: string) =>
  useValue(() => {
    const all = blocks$.get();
    if (!all) return [];
    return Object.keys(all).filter(
      (id) => blocks$[id]?.datasetId.get() === datasetId,
    );
  });

/**
 * Reactively returns the ids of every board holding at least one block built on
 * `datasetId` — the same client-side join as `useBlocksUsingDataset`, collapsed
 * to distinct boards.
 */
export const useBoardsUsingDataset = (datasetId: string) =>
  useValue(() => {
    const all = blocks$.get();
    if (!all) return [];
    const boardIds = new Set<string>();
    for (const id of Object.keys(all)) {
      if (blocks$[id]?.datasetId.get() !== datasetId) continue;
      const boardId = blocks$[id]?.boardId.get();
      if (boardId) boardIds.add(boardId);
    }
    return [...boardIds];
  });

const BlockRow = ({
  blockId,
  onSelect,
}: {
  blockId: string;
  onSelect: (blockId: string) => void;
}) => {
  const name = useValue(blocks$[blockId].name);
  const avatar = useValue(blocks$[blockId].avatar);

  return (
    <button
      type="button"
      onClick={() => onSelect(blockId)}
      className="flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1 text-left hover:bg-accent"
    >
      <AvatarDisplay
        avatar={avatar}
        fallbackIcon="blocks"
        className="shrink-0"
      />
      <span className="min-w-0 flex-1 truncate text-xs">
        {name || "Untitled block"}
      </span>
    </button>
  );
};

const BoardRow = ({
  boardId,
  onSelect,
}: {
  boardId: string;
  onSelect: (boardId: string) => void;
}) => {
  const name = useValue(boards$[boardId].name);
  const avatar = useValue(boards$[boardId].avatar);

  return (
    <button
      type="button"
      onClick={() => onSelect(boardId)}
      className="flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1 text-left hover:bg-accent"
    >
      <AvatarDisplay
        avatar={avatar}
        fallbackIcon="dashboard"
        className="shrink-0"
      />
      <span className="min-w-0 flex-1 truncate text-xs">
        {name || "Untitled dashboard"}
      </span>
    </button>
  );
};

/**
 * Wraps `children` in a tooltip listing every dashboard that renders a block
 * built on `datasetId`. Returns `null` when none do, so callers can use it as a
 * self-gating element. Rows navigate to their board on click.
 */
export const DatasetBoardUsageTooltip = ({
  datasetId,
  children,
}: {
  datasetId: string;
  children: ReactElement;
}) => {
  const boardIds = useBoardsUsingDataset(datasetId);
  const navigate = useNavigate();

  if (boardIds.length === 0) return null;

  return (
    <Tooltip>
      <TooltipTrigger render={children} />
      <TooltipContent
        sideOffset={4}
        className="max-h-64 w-56 max-w-xs overflow-y-auto p-1"
      >
        <div className="flex w-full flex-col gap-0.5">
          <div className="px-2 pt-1 pb-0.5 font-medium text-[10px] text-muted-foreground uppercase tracking-wide">
            Used on {boardIds.length}{" "}
            {boardIds.length === 1 ? "dashboard" : "dashboards"}
          </div>
          {boardIds.map((id) => (
            <BoardRow
              key={id}
              boardId={id}
              onSelect={(boardId) =>
                navigate({ to: "/boards/$boardId", params: { boardId } })
              }
            />
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Wraps `children` in a tooltip listing every block that uses `datasetId`.
 * Returns `null` when no blocks reference the dataset, so callers can use it
 * as a self-gating element. Block rows navigate to their board on click.
 */
export const DatasetUsageTooltip = ({
  datasetId,
  children,
}: {
  datasetId: string;
  children: ReactElement;
}) => {
  const blockIds = useBlocksUsingDataset(datasetId);
  const navigate = useNavigate();

  if (blockIds.length === 0) return null;

  const handleSelect = (blockId: string) => {
    const boardId = blocks$[blockId].boardId.get();
    if (!boardId) return;
    // The board route's `beforeLoad` seeds `boardPage$.selectedBlockId` from
    // `search.blockId`, and `BoardPage` scrolls/focuses the matching grid
    // item — so we just route there with the right URL shape.
    navigate({
      to: "/boards/$boardId",
      params: { boardId },
      search: { blockId },
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger render={children} />
      <TooltipContent
        sideOffset={4}
        className="max-h-64 w-56 max-w-xs overflow-y-auto p-1"
      >
        <div className="flex w-full flex-col gap-0.5">
          <div className="px-2 pt-1 pb-0.5 font-medium text-[10px] text-muted-foreground uppercase tracking-wide">
            Used by {blockIds.length}{" "}
            {blockIds.length === 1 ? "block" : "blocks"}
          </div>
          {blockIds.map((id) => (
            <BlockRow key={id} blockId={id} onSelect={handleSelect} />
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
